import { useEffect, useState } from 'react'
import MainLayout from '../components/MainLayout'
import { useAuth } from '../context/AuthContext'
import apiClient from '../api/client'

interface StripeConfig {
  pricing_table_id: string
  publishable_key: string
}

const SubscriptionPage = () => {
  const { user } = useAuth()
  const [stripeConfig, setStripeConfig] = useState<StripeConfig | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Load Stripe pricing table script
    const script = document.createElement('script')
    script.src = 'https://js.stripe.com/v3/pricing-table.js'
    script.async = true
    document.head.appendChild(script)

    // Fetch Stripe configuration from backend
    const fetchConfig = async () => {
      try {
        const response = await apiClient.get<StripeConfig>('/utils/stripe-config')
        setStripeConfig(response.data)
      } catch (error) {
        console.error('Failed to load Stripe configuration:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchConfig()

    return () => {
      // Cleanup script on unmount
      if (document.head.contains(script)) {
        document.head.removeChild(script)
      }
    }
  }, [])

  if (loading) {
    return (
      <MainLayout>
        <div className="max-w-4xl mx-auto flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </MainLayout>
    )
  }

  if (!stripeConfig) {
    return (
      <MainLayout>
        <div className="max-w-4xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <p className="text-red-600">Failed to load pricing information. Please try again later.</p>
          </div>
        </div>
      </MainLayout>
    )
  }

  return (
    <MainLayout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6 sticky top-0 z-10">
          <div className="flex items-center space-x-3">
            <svg
              className="w-8 h-8 text-primary"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
            </svg>
            <h1 className="text-2xl font-bold text-gray-900">Pricing Plans</h1>
          </div>
        </div>

        {/* Stripe Pricing Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <stripe-pricing-table
            pricing-table-id={stripeConfig.pricing_table_id}
            publishable-key={stripeConfig.publishable_key}
            client-reference-id={user?.id}
            customer-email={user?.email}
          ></stripe-pricing-table>
        </div>
      </div>
    </MainLayout>
  )
}

// Declare Stripe Pricing Table custom element for TypeScript
declare global {
  namespace JSX {
    interface IntrinsicElements {
      'stripe-pricing-table': React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement> & {
          'pricing-table-id': string
          'publishable-key': string
          'client-reference-id'?: string
          'customer-email'?: string
        },
        HTMLElement
      >
    }
  }
}

export default SubscriptionPage
